import NoSSR from 'react-no-ssr';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { connect, useDispatch } from 'react-redux';
import { productAlreadyInCart } from '../../helpers/state/actions/cartActionHelpers';
import nonSubscriptionPurchaseHelpers from '../../helpers/nonSubscriptionPurchase';

import { updateOrderAction } from '../../state/actions/cartActions';
import { addMessage } from '../../state/actions/messagesActions';

import { ApplicationState } from '../../types/state/storeTypes';
import { CartOrderState } from '../../types/state/reducers/cartReducersTypes';
import { AddToCartProps, AddToCartStateProps, AddToCartComponentProps } from '../../types/components/collections/AddToCart';
import { User } from '../../types/api/UsersTypes';
import { SubscriptionDetail } from '../../types/api/SubscriptionTypes';
import { CollectionResponse } from '../../types/api/CollectionsIndexTypes';

import { MainLinkButton } from '../styled/buttons/MainLinkButton';
import SalePriceBeforeDiscount from '../shared/SalePriceBeforeDiscount';

const AddToCart: React.FC<AddToCartProps> = (props) => {
  const { collection, user, cart, authenticated, subscription, mode, closePermanentPurchaseOptionModal} = props;
  const { getButtonText, shouldHideAddToCart, handleNonSubscriptionButtonClick, fetchCollectionPriceData, calculatePriceBeforeDiscount } = nonSubscriptionPurchaseHelpers;
  const dispatch = useDispatch();
  const router = useRouter();

  const [fetchingPrice, setFetchingPrice] = useState<boolean>(false);
  const [price, setPrice] = useState<number>();
  const [originalPrice, setOriginalPrice] = useState<number>();

  const isPurchaseOptionsMode = mode === 'purchase-options-modal';

  // HOTFIX for recent product release - filter out inactive variants
  // This is because we won't be directly selling 2K for recent release.
  // The future fix is to change back-end to compensate for this.
  const variants = collection?.collection_variants?.filter(variant => {
    if (['BW2', 'OW1', 'LSW1', 'WFA1'].includes(collection.sku)) {
      return variant.resolution.includes('4K');
    }
    return true;
  });

  const selectedVariant = variants?.sort((v1, v2) => v2.price - v1.price)[0];

  // need to clear order errors to make sure UI is fresh and clean
  useEffect(() => {
    const order: CartOrderState = cart.order;
    dispatch(
      updateOrderAction({
        ...order,
        error: null
      })
    );
  }, []);

  const fetchPrice = async () => {
    setFetchingPrice(true);
    try {
      const pricesData = await fetchCollectionPriceData(collection as CollectionResponse, user);
      const mostExpensiveVariant = pricesData.sort(
        (v1, v2) => parseFloat(v2.price) - parseFloat(v1.price)
      )[0];
      setPrice(parseFloat(mostExpensiveVariant.price));
      setOriginalPrice(calculatePriceBeforeDiscount(parseFloat(mostExpensiveVariant.price)));
      setFetchingPrice(false);
    } catch (e) {
      setPrice(undefined);
      dispatch(
        addMessage({
          type: 'error',
          body: e.message || 'An error has occured.'
        })
      );
      setFetchingPrice(false);
    }
  };


  useEffect(() => {
    if (!subscription || authenticated === undefined) return;
    fetchPrice();
  }, [
    user?.min_license_id,
    user?.team?.licensee_profile?.license_id,
    subscription?.license?.id,
    authenticated,
  ]);

  function handleClick() {
    if (closePermanentPurchaseOptionModal) closePermanentPurchaseOptionModal();
    handleNonSubscriptionButtonClick(
      router,
      dispatch,
      collection as CollectionResponse,
      cart,
      user,
      subscription,
    );
  }

  if (!isPurchaseOptionsMode && shouldHideAddToCart(collection)) return <></>;

  if (collection.archived) {
    return (
      <div className="py-3">
        <p className="py-4 text-gray-500">This product is no longer available.</p>
      </div>
    );
  }

const determineButton = () => {
  if (authenticated === undefined) return <></>;
  if (!subscription) {
    return (
      <MainLinkButton
        onClick={handleClick}
        className="!h-[56px] !items-center !justify-center !text-center"
        size="full"
        variant="green"
        disabled={cart.loading}
      >
        {getButtonText(user, authenticated)}
      </MainLinkButton>
    )
  }

  return subscribedButton()

}

  const determineButtonWidth = () => isPurchaseOptionsMode ? 'w-full lg:w-[248px] h-[56px]' : 'w-full md:w-[357px]'

  const renderPlaceHolder = () => (
    <div className="rounded-[5px] block bg-white-5 cursor-default border-0 w-full h-[56px]"/>
  );

  const renderButtonContents = (): React.ReactElement => {
    if (!originalPrice) return <>buy for ${price}</>;
    return (
      <span className="flex flex-row gap-1 items-center">
        <span>buy for{' '}</span>
        <SalePriceBeforeDiscount priceBeforeDiscount={originalPrice} className="!text-[#00DA5E] !text-[13px] !leading-[13px] pt-[2px]" />
        <span>{' '}${price}</span>
      </span>
    );
  }

  const subscribedButton = () => {
    if (!price) return renderPlaceHolder();
    return (
      <div className="flex flex-col items-center gap-1">
        <MainLinkButton
          className={`!bg-[#1D462F] hover:!bg-[#255C3E] !bg-opacity-[70%] hover:!bg-opacity-[85%] !text-[#00DA5E] hover:!text-[#00FF6E] ${determineButtonWidth()}`}
          onClick={handleClick}
          disabled={fetchingPrice || cart.loading}
        >
          {renderButtonContents()}
        </MainLinkButton>
        {collection.free_for_member && !isPurchaseOptionsMode && <span className='text-gray-600 text-[14px] leading-[22px]'>Get permanent access to the collection</span>}
      </div>
    );
  }

  // placeholder until we pull session
  if (authenticated === undefined) return renderPlaceHolder();

  return (
      <div>
      {!productAlreadyInCart(cart?.order?.items?.products, selectedVariant) ?
        determineButton()
        : (
          <div className="rounded-[5px] block px-2 text-white bg-white-5 cursor-default color-white border-0 w-full uppercase text-[16px] sm:text-[17px] text-center font-[Eurostile] !pt-[18px] !pb-[20px] font-semibold">
            This Collection is in your Cart
          </div>
        )}
      </div>
  );
}

const mapStateToProps = (state: ApplicationState): AddToCartStateProps => ({
  cart: state.cart,
  user: state.auth.user as User,
  authenticated: state.auth.authenticated,
  subscription: state.auth.subscription as SubscriptionDetail
});

const ConnectedAddToCart = connect(mapStateToProps)(AddToCart);

export default (props: AddToCartComponentProps) => (
  <NoSSR>
    <ConnectedAddToCart {...props} />
  </NoSSR>
);
